<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.county, 'Județ')"
      :items="counties"
      item-title="name"
      name="county"
      item-value="id"
      v-model="selectedCounty"
      :rules="[v => !!v || translation(translationList.required, 'Acest câmp este obligatoriu')]"
      return-object
      :loading="loading"
      :disabled="loading"
      :readonly="isReadOnly"
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500" v-if="isReadOnly">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" class="cursor-pointer" aria-label="info">mdi-information</v-icon>
        </template>
        <span>
            {{ translation(translationList.county_info, 'Județul a fost autocompletat in baza numarului de inmatriculare') }}
        </span>
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted, toRefs, watch} from 'vue';
import {useAddressStore} from '~/plugins/store/address';
import {useTranslationStore, translation} from '~/plugins/store/translations';
import type {County} from '~/types/Address';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => County | null,
      default: () => null,
      required: false
    },
    addressIndex: Number as () => number | null,
    isCountyReadOnly: Boolean as () => boolean,
  },
  emits: ['update:modelValue', 'validation-address-status'],
  setup(props, {emit}) {
    const {modelValue, addressIndex} = toRefs(props);
    const isReadOnly = computed(() => props.isCountyReadOnly);
    const addressStore = useAddressStore();
    const translationStore = useTranslationStore();
    const selectedCounty = ref(modelValue.value ? modelValue.value : null);
    const counties = ref<County[]>([]);
    const translationList = ref<TranslationList>({});
    let initialSetupDone = false;
    const error = ref([] as string[]);
    const loading = ref(false);

    onMounted(async () => {
      loading.value = true;
      counties.value = await addressStore.getCounties();
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      initialSetupDone = true;
      const isValid = !!selectedCounty.value;
      emit('validation-address-status', {
        field: Number.isInteger(addressIndex.value) ? `county-${addressIndex.value}` : 'county',
        isValid
      });
      loading.value = false;

      // Listen for validate events
      window.addEventListener('validate-address', handleValidationEvent);
      window.addEventListener('validate-travel', handleValidationEvent);
      window.addEventListener('validate-house', handleValidationEvent);
    });

    const handleValidationEvent = (data: any) => {
      if (data.detail && data.detail === (Number.isInteger(addressIndex.value) ? `county-${addressIndex.value}` : 'county')) {
        if (!selectedCounty.value) {
          error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        } else {
          error.value = [];
          emit('validation-address-status', {
            field: Number.isInteger(addressIndex.value) ? `county-${addressIndex.value}` : 'county',
            isValid: true
          });
        }
      }
    };

    watch(selectedCounty, (newCounty) => {
      if (initialSetupDone) {
        emit('update:modelValue', newCounty);
      }
    });

    watch(modelValue, (newValue) => {
      error.value = [];
      if (newValue) {
        selectedCounty.value = newValue;
      }
      const isValid = !!newValue;
      emit('validation-address-status', {
        field: Number.isInteger(addressIndex.value) ? `county-${addressIndex.value}` : 'county',
        isValid
      });
    });

    return {
      selectedCounty,
      translationList,
      translation,
      counties,
      error,
      loading,
      isReadOnly
    };
  },
});
</script>